import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { siteSwitches } from "../../api/endpoints/siteSwitches";
import { qk } from "../../api/queryKeys";
import type { PortIn, PortMode, PortOut } from "../../api/types/siteSwitch";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { SelectField } from "../../components/forms/SelectField";
import { NumberField } from "../../components/forms/NumberField";

const MODE_OPTIONS = [
  { value: "access", label: "Access" },
  { value: "trunk", label: "Trunk" },
  { value: "uplink", label: "Uplink" },
  { value: "maintenance", label: "Maintenance" },
] as const;

interface PortDraft {
  number: number | null; // create-only
  mode: PortMode;
  name: string;
  native_vlan_tag: number | null;
  tagged_vlan_tags: number[];
  notes: string;
}

export function SwitchPortForm({
  siteSlug,
  switchSlug,
  port,
  options,
  canWrite,
  onClose,
}: {
  siteSlug: string;
  switchSlug: string;
  port?: PortOut;
  options: { value: number; label: string }[];
  canWrite: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PortDraft>(() => toDraft(port));
  const set = <K extends keyof PortDraft>(k: K, v: PortDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: (body: PortIn) => {
      const number = port ? port.number : (draft.number as number);
      return siteSwitches.putPort(siteSlug, switchSlug, number, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.siteSwitchPorts(siteSlug, switchSlug) });
      void qc.invalidateQueries({ queryKey: qk.siteSwitch(siteSlug, switchSlug) });
      onClose();
    },
  });

  const numberError = port
    ? null // edit: number is fixed, no field
    : draft.number == null
      ? "Required."
      : !Number.isInteger(draft.number) || draft.number < 1
        ? "Must be a whole number ≥ 1."
        : null;
  const isValid = !numberError;
  const disabled = !canWrite || save.isPending;

  const vlanOptions = [
    { value: "", label: "— none —" },
    ...options.map((o) => ({ value: String(o.value), label: o.label })),
  ];

  const toggleTag = (tag: number) =>
    set(
      "tagged_vlan_tags",
      draft.tagged_vlan_tags.includes(tag)
        ? draft.tagged_vlan_tags.filter((t) => t !== tag)
        : [...draft.tagged_vlan_tags, tag],
    );

  return (
    <FormShell
      submitLabel={port ? "Save port" : "Add port"}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid || disabled) return;
        const orNull = (s: string) => (s.trim() === "" ? null : s.trim());
        save.mutate({
          mode: draft.mode,
          name: orNull(draft.name),
          native_vlan_tag: draft.native_vlan_tag,
          tagged_vlan_tags: draft.tagged_vlan_tags,
          notes: orNull(draft.notes),
        });
      }}
      onCancel={onClose}
      pending={save.isPending}
      submitDisabled={!canWrite || !isValid}
      error={save.error}
      errorTitle="Port save failed"
    >
      {port ? (
        <p style={{ fontSize: "0.85rem", color: "var(--text-dim)" }}>
          Port number: <code>{port.number}</code> (fixed)
        </p>
      ) : (
        <NumberField
          label="Port number"
          value={draft.number}
          onChange={(v) => set("number", v)}
          min={1}
          error={numberError}
          disabled={disabled}
        />
      )}
      <SelectField
        label="Mode"
        value={draft.mode}
        onChange={(v) => set("mode", v as PortMode)}
        options={MODE_OPTIONS}
        disabled={disabled}
      />
      <TextField label="Name" value={draft.name} onChange={(v) => set("name", v)} placeholder="ap-trusted" disabled={disabled} />
      <SelectField
        label="Native VLAN"
        value={draft.native_vlan_tag == null ? "" : String(draft.native_vlan_tag)}
        onChange={(v) => set("native_vlan_tag", v === "" ? null : Number(v))}
        options={vlanOptions}
        disabled={disabled}
      />
      <div style={{ display: "grid", gap: 6 }}>
        <span style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>Tagged VLANs</span>
        {options.length === 0 ? (
          <span style={{ fontSize: "0.85rem", color: "var(--text-dim)" }}>No VLAN template entries.</span>
        ) : (
          <div style={{ display: "grid", gap: 4 }}>
            {options.map((o) => (
              <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem" }}>
                <input
                  type="checkbox"
                  checked={draft.tagged_vlan_tags.includes(o.value)}
                  disabled={disabled}
                  onChange={() => toggleTag(o.value)}
                />
                {o.label}
              </label>
            ))}
          </div>
        )}
      </div>
      <TextField label="Notes" value={draft.notes} onChange={(v) => set("notes", v)} disabled={disabled} />
    </FormShell>
  );
}

function toDraft(port?: PortOut): PortDraft {
  return {
    number: port?.number ?? null,
    mode: port?.mode ?? "access",
    name: port?.name ?? "",
    native_vlan_tag: port?.native_vlan_tag ?? null,
    tagged_vlan_tags: port?.tagged_vlan_tags ?? [],
    notes: port?.notes ?? "",
  };
}
